import { MedicalAnalysisService } from "./medicalAnalysis";
import { ICD11Service, ICD11Entry } from "./icd11Service";
import { MedicalSearchService } from "./medicalSearch";
import { IntelligentScoringService } from "./intelligentScoring";

interface SelfCareRecommendation {
  category: string;
  title: string;
  description: string;
  actions: string[];
  duration: string;
  warnings: string[];
  confidenceLevel: number;
  evidenceBased: boolean;
  targetSymptoms: string[];
  contraindications: string[];
}

export class SelfCareGuidanceService {
  /**
   * Generate intelligent, evidence-based self-care recommendations for mild symptoms (scores 0-40)
   */
  async generateSelfCareRecommendations(
    aiScore: number,
    category: string, 
    selectedSymptoms: string[], 
    additionalSymptoms: string[],
    answers: Record<string, any>,
    medicalEvidence?: any[]
  ): Promise<SelfCareRecommendation[]> {
    // Only provide self-care guidance for mild scores (0-40)
    if (aiScore > 40) {
      return [];
    }

    const allSymptoms = [...selectedSymptoms, ...additionalSymptoms];
    
    try {
      // Get intelligent analysis for symptoms
      const [medicalAnalysis, icd11Data, symptomResearch] = await Promise.all([
        MedicalAnalysisService.getEnhancedAnalysis(allSymptoms, aiScore / 10, answers.symptom_duration || ''),
        this.getRelevantICD11Conditions(allSymptoms, category),
        MedicalSearchService.searchRelevantKnowledge(allSymptoms, [], ['self_care', 'treatment'], 10)
      ]);

      // Generate dynamic recommendations based on actual medical data
      const recommendations = await this.generateEvidenceBasedRecommendations(
        allSymptoms,
        aiScore,
        medicalAnalysis,
        icd11Data,
        symptomResearch,
        answers
      );

      // Validate safety of recommendations
      const safeRecommendations = this.validateSafetyThresholds(recommendations, aiScore, allSymptoms);

      return safeRecommendations;

    } catch (error) {
      console.error('Error generating intelligent self-care recommendations:', error);
      // Fallback to conservative recommendations
      return this.generateConservativeFallback(allSymptoms, aiScore);
    }
  }

  private async getRelevantICD11Conditions(symptoms: string[], category: string): Promise<ICD11Entry[]> {
    try {
      const field = this.mapCategoryToField(category);
      const icd11Response = await ICD11Service.mapResponsesToICD11({
        field,
        symptoms,
        duration: 'recent',
        pattern: 'stable',
        impact: 3 // Assume mild impact for self-care candidates
      });

      // Focus on mild conditions suitable for self-care
      return [...icd11Response.primaryConditions, ...icd11Response.secondaryConditions]
        .filter(condition => {
          // Check if it's a mild condition or unknown severity
          const severity = condition.severity_level;
          return !severity || severity === 'low' || severity.includes('mild');
        });
        
    } catch (error) {
      console.error('Error fetching ICD11 conditions:', error);
      return [];
    }
  }

  private async generateEvidenceBasedRecommendations(
    symptoms: string[],
    aiScore: number,
    medicalAnalysis: any,
    icd11Conditions: ICD11Entry[],
    research: any[],
    answers: Record<string, any>
  ): Promise<SelfCareRecommendation[]> {
    const recommendations: SelfCareRecommendation[] = [];

    // Generate recommendations based on ICD-11 mild conditions
    for (const condition of icd11Conditions) {
      const conditionRecommendation = await this.generateConditionSpecificRecommendations(
        condition,
        symptoms,
        aiScore,
        research
      );
      if (conditionRecommendation) {
        recommendations.push(conditionRecommendation);
      }
    }

    // Generate symptom-specific recommendations
    const symptomRecommendations = await this.generateSymptomSpecificRecommendations(
      symptoms,
      aiScore,
      research,
      answers
    );
    recommendations.push(...symptomRecommendations);

    // Generate evidence-based general recommendations
    const generalRecommendations = await this.generateEvidenceBasedGeneralRecommendations(
      medicalAnalysis,
      symptoms,
      aiScore
    );
    recommendations.push(...generalRecommendations);

    // Remove duplicates and prioritize by evidence quality
    return this.deduplicateAndPrioritize(recommendations);
  }

  private async generateConditionSpecificRecommendations(
    condition: ICD11Entry,
    symptoms: string[],
    aiScore: number,
    research: any[]
  ): Promise<SelfCareRecommendation | null> {
    // Find research supporting self-care for this condition
    const relevantResearch = research.filter(study => 
      study.title.toLowerCase().includes(condition.title.toLowerCase().split(' ')[0]) ||
      study.condition_tags?.some((tag: string) => 
        tag.toLowerCase().includes(condition.title.toLowerCase().split(' ')[0])
      )
    );

    if (relevantResearch.length === 0) return null;

    // Extract self-care actions from research
    const actions = this.extractSelfCareActionsFromResearch(relevantResearch, condition.title);
    
    if (actions.length === 0) return null;

    return {
      category: `${condition.title} Management`,
      title: `Evidence-Based Care for ${condition.title}`,
      description: condition.definition || `Self-care management for ${condition.title} based on current medical literature`,
      actions,
      duration: this.calculateDynamicDuration(condition.title, aiScore),
      warnings: this.generateDynamicWarnings(condition.title, symptoms),
      confidenceLevel: this.calculateConfidence(relevantResearch.length, condition.severity_level),
      evidenceBased: true,
      targetSymptoms: symptoms.filter(symptom => 
        condition.symptom_mappings?.some(mapping => 
          mapping.toLowerCase().includes(symptom.toLowerCase())
        ) || false
      ),
      contraindications: this.generateContraindications(condition.title, symptoms)
    };
  }

  private async generateSymptomSpecificRecommendations(
    symptoms: string[],
    aiScore: number,
    research: any[],
    answers: Record<string, any>
  ): Promise<SelfCareRecommendation[]> {
    const recommendations: SelfCareRecommendation[] = [];

    for (const symptom of symptoms) {
      // Find research specific to this symptom
      const symptomResearch = research.filter(study =>
        study.title.toLowerCase().includes(symptom.toLowerCase()) ||
        study.symptom_tags?.includes(symptom.toLowerCase()) ||
        study.content?.toLowerCase().includes(symptom.toLowerCase())
      );

      if (symptomResearch.length >= 2) { // Require at least 2 studies for evidence-based recommendation
        const actions = this.extractSelfCareActionsFromResearch(symptomResearch, symptom);
        
        if (actions.length > 0) {
          recommendations.push({
            category: `${symptom} Management`,
            title: `Targeted Care for ${symptom}`,
            description: `Evidence-based self-care specifically targeting ${symptom.toLowerCase()}`,
            actions,
            duration: this.calculateSymptomSpecificDuration(symptom, aiScore, answers),
            warnings: this.generateSymptomSpecificWarnings(symptom),
            confidenceLevel: this.calculateConfidence(symptomResearch.length, 'medium'),
            evidenceBased: true,
            targetSymptoms: [symptom],
            contraindications: this.generateSymptomContraindications(symptom)
          });
        }
      }
    }

    return recommendations;
  }

  private async generateEvidenceBasedGeneralRecommendations(
    medicalAnalysis: any,
    symptoms: string[],
    aiScore: number
  ): Promise<SelfCareRecommendation[]> {
    const recommendations = medicalAnalysis.recommendations || [];
    const actions = this.extractGeneralSelfCareActions(recommendations, symptoms);

    if (actions.length === 0) return [];

    return [{
      category: 'General Wellness',
      title: 'Evidence-Based General Care',
      description: `General wellness measures supported by your symptom analysis and medical evidence`,
      actions,
      duration: this.calculateAdaptiveDuration(aiScore, symptoms.length),
      warnings: this.generateAdaptiveWarnings(symptoms, aiScore),
      confidenceLevel: medicalAnalysis.evidenceLevel === 'high' ? 0.85 : 0.70,
      evidenceBased: true,
      targetSymptoms: symptoms,
      contraindications: this.generateGeneralContraindications(symptoms)
    }];
  }

  private extractSelfCareActionsFromResearch(research: any[], condition: string): string[] {
    const actions: string[] = [];
    const actionKeywords = [
      'self-care', 'home treatment', 'lifestyle', 'rest', 'hydration', 'nutrition',
      'exercise', 'stress management', 'sleep', 'relaxation', 'heat therapy',
      'cold therapy', 'gentle', 'gradual', 'monitor', 'track'
    ];

    for (const study of research) {
      const content = (study.content || study.summary || '').toLowerCase();
      const title = study.title.toLowerCase();
      
      // Extract specific action phrases
      actionKeywords.forEach(keyword => {
        const keywordIndex = content.indexOf(keyword);
        if (keywordIndex !== -1) {
          // Extract sentence containing the keyword
          const sentences = content.split(/[.!?]/);
          const relevantSentence = sentences.find(sentence => sentence.includes(keyword));
          if (relevantSentence && relevantSentence.length < 200) {
            const cleanAction = this.cleanActionText(relevantSentence.trim());
            if (cleanAction && !actions.includes(cleanAction)) {
              actions.push(cleanAction);
            }
          }
        }
      });
    }

    // If no specific actions found, generate evidence-informed actions
    if (actions.length === 0) {
      actions.push(...this.generateEvidenceInformedActions(condition, research));
    }

    return actions.slice(0, 6); // Limit to 6 most relevant actions
  }

  private cleanActionText(text: string): string {
    // Clean and format action text
    return text
      .replace(/[^a-zA-Z0-9\s,.-]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .charAt(0).toUpperCase() + text.slice(1);
  }

  private generateEvidenceInformedActions(condition: string, research: any[]): string[] {
    const conditionLower = condition.toLowerCase();
    
    // Generate actions based on condition type and available research
    const actions: string[] = [];
    
    if (conditionLower.includes('headache') || conditionLower.includes('pain')) {
      actions.push(
        'Apply cold or warm compress for 15-20 minutes as tolerated',
        'Ensure adequate hydration (8-10 glasses of water daily)',
        'Maintain regular sleep schedule and stress management'
      );
    }
    
    if (conditionLower.includes('respiratory') || conditionLower.includes('cough')) {
      actions.push(
        'Use humidifier or steam inhalation for comfort',
        'Stay well-hydrated with warm, non-caffeinated fluids',
        'Rest and avoid irritants like smoke'
      );
    }
    
    if (conditionLower.includes('digestive') || conditionLower.includes('stomach')) {
      actions.push(
        'Follow bland diet (BRAT: bananas, rice, applesauce, toast)',
        'Stay hydrated with small, frequent sips of clear fluids',
        'Rest and avoid fatty or spicy foods temporarily'
      );
    }

    // Add general evidence-based actions
    actions.push(
      'Monitor symptoms and note any changes or patterns',
      'Maintain good sleep hygiene and stress management',
      'Engage in gentle activities as tolerated'
    );

  return actions.slice(0, 4);
  }

  // Helper methods for intelligent calculations

  private validateSafetyThresholds(
    recommendations: SelfCareRecommendation[], 
    aiScore: number, 
    symptoms: string[]
  ): SelfCareRecommendation[] {
    // Filter out recommendations that may not be safe for higher scores
    return recommendations.filter(rec => {
      // Additional safety checks for specific symptoms
      const hasRedFlagSymptoms = symptoms.some(symptom => 
        symptom.toLowerCase().includes('severe') ||
        symptom.toLowerCase().includes('blood') ||
        symptom.toLowerCase().includes('chest pain')
      );
      
      if (hasRedFlagSymptoms) return false;
      if (aiScore > 35) return rec.evidenceBased; // Only evidence-based recommendations for higher scores
      
      return true;
    });
  }

  private generateConservativeFallback(symptoms: string[], aiScore: number): SelfCareRecommendation[] {
    return [{
      category: 'Conservative Care',
      title: 'Safe Self-Care Measures',
      description: 'Conservative self-care measures while monitoring for changes',
      actions: [
        'Rest and avoid strenuous activities',
        'Stay well-hydrated with clear fluids',
        'Monitor symptoms closely for any changes',
        'Consider gentle activities as tolerated'
      ],
      duration: 'Monitor for 24-48 hours',
      warnings: [
        'Seek medical care if symptoms worsen',
        'Contact healthcare provider if no improvement in 2-3 days',
        'Get immediate help for any severe or concerning symptoms'
      ],
      confidenceLevel: 0.60,
      evidenceBased: false,
      targetSymptoms: symptoms,
      contraindications: ['Severe symptoms', 'Worsening condition']
    }];
  }

  private mapCategoryToField(category: string): string {
    const mapping: Record<string, string> = {
      'Head and Nerves': 'head/neurological',
      'Respiratory': 'chest/breathing',
      'Digestive': 'stomach/digestion',
      'Musculoskeletal': 'muscle/bones',
      'Skin': 'skin',
      'Heart and Blood': 'cardiovascular',
      'Mental Health': 'mental health'
    };
    return mapping[category] || 'general';
  }

  private deduplicateAndPrioritize(recommendations: SelfCareRecommendation[]): SelfCareRecommendation[] {
    // Remove duplicates based on title similarity
    const unique = recommendations.filter((rec, index, arr) => 
      index === arr.findIndex(r => r.title === rec.title)
    );
    
    // Sort by evidence quality and confidence
    return unique.sort((a, b) => {
      if (a.evidenceBased !== b.evidenceBased) return a.evidenceBased ? -1 : 1;
      return b.confidenceLevel - a.confidenceLevel;
    }).slice(0, 3); // Return top 3 recommendations
  }

  private calculateDynamicDuration(condition: string, aiScore: number): string {
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('acute') || aiScore > 30) {
      return 'Monitor for 24-48 hours, seek care if no improvement';
    } else if (conditionLower.includes('chronic')) {
      return 'Continue for 1-2 weeks with regular monitoring';
    }
    
    return 'Continue for 3-5 days with daily assessment';
  }

  private generateDynamicWarnings(condition: string, symptoms: string[]): string[] {
    const warnings = [
      'Seek care if symptoms worsen significantly',
      'Contact healthcare provider if no improvement as expected'
    ];
    
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('respiratory')) {
      warnings.push('Get immediate help for difficulty breathing or chest pain');
    } else if (conditionLower.includes('digestive')) {
      warnings.push('Seek care for severe abdominal pain or signs of dehydration');
    } else if (conditionLower.includes('headache')) {
      warnings.push('Get immediate help for sudden, severe headache or vision changes');
    }
    
    return warnings;
  }

  private calculateConfidence(evidenceCount: number, severityLevel?: string): number {
    let confidence = 0.5; // Base confidence
    
    if (evidenceCount >= 5) confidence += 0.3;
    else if (evidenceCount >= 3) confidence += 0.2;
    else if (evidenceCount >= 1) confidence += 0.1;
    
    if (severityLevel === 'low') confidence += 0.1;
    
    return Math.min(0.95, confidence);
  }

  private generateContraindications(condition: string, symptoms: string[]): string[] {
    const contraindications = ['Severe or worsening symptoms'];
    
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('cardiac')) {
      contraindications.push('Chest pain', 'Difficulty breathing', 'Dizziness');
    } else if (conditionLower.includes('respiratory')) {
      contraindications.push('Severe breathing difficulty', 'High fever');
    }
    
    return contraindications;
  }

  private calculateSymptomSpecificDuration(symptom: string, aiScore: number, answers: Record<string, any>): string {
    const duration = answers.symptom_duration || '';
    
    if (duration.includes('acute') || aiScore > 30) {
      return `Monitor ${symptom.toLowerCase()} for 24-48 hours`;
    }
    
    return `Continue care for ${symptom.toLowerCase()} for 3-5 days`;
  }

  private generateSymptomSpecificWarnings(symptom: string): string[] {
    const symptomLower = symptom.toLowerCase();
    const warnings = [`Seek care if ${symptomLower} worsens significantly`];
    
    if (symptomLower.includes('pain')) {
      warnings.push('Contact provider for severe or increasing pain');
    } else if (symptomLower.includes('fever')) {
      warnings.push('Seek care for high fever (>101°F) or prolonged fever');
    }
    
    return warnings;
  }

  private generateSymptomContraindications(symptom: string): string[] {
    const symptomLower = symptom.toLowerCase();
    
    if (symptomLower.includes('chest') || symptomLower.includes('breathing')) {
      return ['Severe chest pain', 'Difficulty breathing', 'Rapid heart rate'];
    } else if (symptomLower.includes('abdominal') || symptomLower.includes('stomach')) {
      return ['Severe abdominal pain', 'Blood in vomit or stool', 'Signs of dehydration'];
    }
    
    return ['Severe symptoms', 'Rapid worsening'];
  }

  private extractGeneralSelfCareActions(recommendations: string[], symptoms: string[]): string[] {
    const actions: string[] = [];
    
    // Extract actionable recommendations
    recommendations.forEach(rec => {
      if (rec.toLowerCase().includes('rest')) {
        actions.push('Ensure adequate rest and sleep');
      }
      if (rec.toLowerCase().includes('hydrat')) {
        actions.push('Maintain good hydration');
      }
      if (rec.toLowerCase().includes('monitor')) {
        actions.push('Monitor symptoms and track changes');
      }
    });
    
    // Add general wellness actions
    if (actions.length === 0) {
      actions.push(
        'Rest and avoid overexertion',
        'Stay hydrated with appropriate fluids',
        'Monitor symptoms daily',
        'Practice stress management'
      );
    }
    
    return actions.slice(0, 5);
  }

  private calculateAdaptiveDuration(aiScore: number, symptomCount: number): string {
    if (aiScore > 30 || symptomCount > 3) {
      return 'Monitor for 24-48 hours, reassess frequently';
    } else if (aiScore > 20) {
      return 'Continue for 2-3 days with daily assessment';
    }
    
    return 'Continue for 3-5 days with regular monitoring';
  }

  private generateAdaptiveWarnings(symptoms: string[], aiScore: number): string[] {
    const warnings = ['Seek care if symptoms worsen'];
    
    if (aiScore > 30) {
      warnings.push('Consider medical evaluation if no improvement in 24-48 hours');
    } else {
      warnings.push('Contact provider if no improvement after 3-5 days');
    }
    
    // Add symptom-specific warnings
    const hasRespiratory = symptoms.some(s => s.toLowerCase().includes('breathing') || s.toLowerCase().includes('cough'));
    if (hasRespiratory) {
      warnings.push('Get immediate help for severe breathing difficulties');
    }
    
    return warnings;
  }

  private generateGeneralContraindications(symptoms: string[]): string[] {
    const contraindications = ['Severe or rapidly worsening symptoms'];
    
    const hasCardiac = symptoms.some(s => s.toLowerCase().includes('chest') || s.toLowerCase().includes('heart'));
    if (hasCardiac) {
      contraindications.push('Chest pain or cardiac symptoms');
    }
    
    return contraindications;
  }

  private getGeneralRecommendations(): SelfCareRecommendation[] {
    return [{
      category: 'General Wellness',
      title: 'General Self-Care',
      description: 'General wellness measures that support recovery from mild symptoms',
      actions: [
        'Ensure adequate rest and sleep (7-9 hours nightly)',
        'Stay well-hydrated throughout the day',
        'Eat nutritious, balanced meals',
        'Engage in light physical activity as tolerated',
        'Practice stress management techniques',
        'Monitor symptoms and note any changes',
        'Maintain social connections and support'
      ],
      duration: 'Continue for 3-5 days',
      warnings: [
        'Seek care if symptoms worsen or new symptoms develop',
        'Consult provider if no improvement after 1 week',
        'Get immediate help if symptoms become severe'
      ],
      confidenceLevel: 0.70,
      evidenceBased: false,
      targetSymptoms: [],
      contraindications: ['Severe symptoms']
    }];
  }

  getEscalationTriggers(category: string): string[] {
    const baseTrigers = [
      'Symptoms significantly worsen instead of improving',
      'New or concerning symptoms develop',
      'Unable to perform daily activities',
      'Symptoms persist beyond expected timeframe'
    ];

    const categorySpecificTriggers: Record<string, string[]> = {
      'Head and Nerves': [
        'Severe, sudden headache unlike any before',
        'Headache with fever, stiff neck, or rash',
        'Vision changes or loss',
        'Confusion or difficulty speaking',
        'Persistent dizziness affecting balance'
      ],
      'Respiratory': [
        'Increasing difficulty breathing',
        'Chest pain with breathing',
        'High fever (>101°F/38.3°C)',
        'Coughing up blood',
        'Inability to speak in full sentences'
      ],
      'Digestive': [
        'Severe, persistent abdominal pain',
        'Signs of dehydration (dizziness, dark urine)',
        'Blood in vomit or stool',
        'High fever with digestive symptoms',
        'Unable to keep fluids down for 24+ hours'
      ],
      'Musculoskeletal': [
        'Severe pain that limits all movement',
        'Numbness or tingling in extremities',
        'Obvious deformity or inability to bear weight',
        'Signs of infection (fever, redness, warmth)',
        'No improvement after 1 week of self-care'
      ]
    };

    return [...baseTrigers, ...(categorySpecificTriggers[category] || [])];
  }
}

export const selfCareGuidanceService = new SelfCareGuidanceService();